import { useEffect } from 'react';

/**
 * useScrollReveal hook
 * - Elements already in the viewport on load are revealed immediately (with a small delay after data loads).
 * - Elements below the fold are revealed by IntersectionObserver when scrolled into view.
 */
export default function useScrollReveal() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Helper: check if element is already in the viewport
    const isInViewport = (el) => {
      const rect = el.getBoundingClientRect();
      return (
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0
      );
    };

    // 1. Setup IntersectionObserver for off-screen elements
    const io = window.IntersectionObserver ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--active');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }) : null;

    // 2. Function to scan for new reveals
    const scan = () => {
      const elements = document.querySelectorAll('.reveal:not(.reveal--active)');
      elements.forEach((el) => {
        if (isInViewport(el)) {
          // Already visible — reveal immediately (tiny rAF delay so CSS is ready)
          requestAnimationFrame(() => {
            el.classList.add('reveal--active');
          });
        } else if (io) {
          // Off-screen — let IntersectionObserver handle it
          io.observe(el);
        } else {
          // Fallback if IntersectionObserver is not supported
          el.classList.add('reveal--active');
        }
      });
    };

    // Initial scan
    scan();

    // 3. MutationObserver to catch dynamically loaded content immediately
    let observer = null;
    if (window.MutationObserver) {
      observer = new MutationObserver(() => {
        scan();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // 4. Also scan on scroll/click/resize to be highly reactive
    window.addEventListener('scroll', scan, { passive: true });
    window.addEventListener('resize', scan, { passive: true });
    window.addEventListener('click', scan);

    return () => {
      if (io) io.disconnect();
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', scan);
      window.removeEventListener('resize', scan);
      window.removeEventListener('click', scan);
    };
  }, []);
}
